import React from 'react';
import { PageTransition } from "@/components/PageTransition";
import { useOnInView } from 'react-intersection-observer';
import { useSEO } from "@/lib/seo/useSEO";

interface Props {
  onScrollToBottom?: () => void;
}

export const TermsAndPrivacyPolicyPage: React.FC<Props> = ({ onScrollToBottom }) => {
  const trackingRef = useOnInView(
    (inView) => {
      if (inView) {
        onScrollToBottom?.();
      }
    },
    {
      threshold: 0.5,
      triggerOnce: true,
    },
  );

  useSEO({
    title: "Terms and Privacy Policy - KeptScene",
    description: "Read KeptScene's Terms and Conditions and Privacy Policy. Learn about our data collection, and user responsibilities.",
    keywords: "terms and conditions, privacy policy, KeptScene terms, data privacy",
  });

  return (
    <PageTransition>
      <div className="min-h-screen-safe-area bg-background" data-element="terms-and-privacy-policy-page">
        <div className="px-8 py-12 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-vintage-red mb-4">
              Terms and Conditions
            </h1>
            <p className="text-vintage-black text-lg">
              Last updated: 11 December 2025
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <p className="text-vintage-black text-lg leading-relaxed mb-4">
              Please read these Terms and Conditions carefully before using the Photobooth and AI-powered image processing services provided by KEPT SCENE.
            </p>
            <p className="text-vintage-black text-lg leading-relaxed">
              By using our services, you agree to be bound by all of the terms and conditions set forth herein.
            </p>
          </div>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              1. Purpose of the Service
            </h2>
            <p className="text-vintage-black text-lg leading-relaxed">
              Our website provides users with the ability to take photos, edit images, change or remove backgrounds, add filters, stickers, or effects, as well as use AI models to process images according to user preferences.
            </p>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              2. Data Collection and Processing
            </h2>
            
            <h3 className="text-xl font-semibold text-vintage-black mb-3">
              2.1 Types of Data Collected
            </h3>
            <ul className="text-vintage-black text-lg leading-relaxed mb-4 space-y-2">
              <li>• We collect only the data necessary to improve the quality of our services.</li>
              <li>• We do not collect or store any images, videos, or personally identifiable information of users.</li>
            </ul>

            <h3 className="text-xl font-semibold text-vintage-black mb-3">
              2.2 Use of AI
            </h3>
            <ul className="text-vintage-black text-lg leading-relaxed mb-4 space-y-2">
              <li>• Your images may be processed through AI systems for purposes such as background removal, scene replacement, image decoration, or quality enhancement.</li>
              <li>• AI is used solely to process your images according to your instructions and is not used for further AI model training.</li>
            </ul>

            <h3 className="text-xl font-semibold text-vintage-black mb-3">
              2.3 Data Retention Period
            </h3>
            <ul className="text-vintage-black text-lg leading-relaxed space-y-2">
              <li>• We retain your image files for one (1) week after service usage solely for refund verification purposes. After this period, all images will be permanently deleted.</li>
              <li>• Once the user selects "Download," the image is considered outside our system and responsibility.</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              3. Image and Content Ownership
            </h2>
            
            <h3 className="text-xl font-semibold text-vintage-black mb-3">
              3.1 User Ownership
            </h3>
            <ul className="text-vintage-black text-lg leading-relaxed mb-4 space-y-2">
              <li>• Ownership of the original images remains with the user.</li>
              <li>• The user grants the service provider permission to use the images solely for processing as requested.</li>
            </ul>

            <h3 className="text-xl font-semibold text-vintage-black mb-3">
              3.2 Ownership of Provided Assets
            </h3>
            <ul className="text-vintage-black text-lg leading-relaxed space-y-2">
              <li>• All frames, filters, stickers, graphics, and other elements created and provided by KEPT SCENE are the property of KEPT SCENE.</li>
              <li>• Users may not reproduce, sell, modify, distribute, or use these assets for commercial purposes without prior written consent.</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              4. Permitted Use
            </h2>
            
            <p className="text-vintage-black text-lg font-semibold mb-3">
              Users may:
            </p>
            <ul className="text-vintage-black text-lg leading-relaxed mb-4 space-y-2">
              <li>• Take and download photos for personal use</li>
              <li>• Share images on social media platforms</li>
              <li>• Use images for activities or events in accordance with their intended purpose</li>
            </ul>

            <p className="text-vintage-black text-lg font-semibold mb-3">
              Users may not:
            </p>
            <ul className="text-vintage-black text-lg leading-relaxed space-y-2">
              <li>• Upload images that infringe upon the intellectual property rights of others</li>
              <li>• Use images to harm, defame, or engage in unlawful activities</li>
              <li>• Attempt to hack, copy, reverse-engineer, or modify the system or services</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              5. Privacy
            </h2>
            <ul className="text-vintage-black text-lg leading-relaxed space-y-2">
              <li>• We do not share user images with third parties.</li>
              <li>• We do not use user images for promotional purposes or as portfolio examples without prior consent.</li>
              <li>• While we implement industry-standard security measures, users acknowledge that data transmission over the internet may involve certain risks.</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              6. User Responsibilities
            </h2>
            <p className="text-vintage-black text-lg leading-relaxed mb-3">
              Users confirm that:
            </p>
            <ul className="text-vintage-black text-lg leading-relaxed space-y-2">
              <li>• All uploaded images are owned by them or they have lawful rights to use them</li>
              <li>• All individuals appearing in the images have given consent for AI processing</li>
              <li>• The service will not be used for fraudulent or illegal purposes</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              7. Service Provider Liability
            </h2>
            <p className="text-vintage-black text-lg leading-relaxed mb-3">
              The service provider shall not be liable for:
            </p>
            <ul className="text-vintage-black text-lg leading-relaxed mb-4 space-y-2">
              <li>• Any damages resulting from AI processing based on user instructions</li>
              <li>• Failures caused by internet connectivity or user devices</li>
              <li>• Any use of user images outside the system after download</li>
            </ul>
            <p className="text-vintage-black text-lg leading-relaxed">
              The service is provided on an "as-is" basis without any guarantee of 100% accuracy.
            </p>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              8. Changes to the Terms
            </h2>
            <p className="text-vintage-black text-lg leading-relaxed mb-2">
              We reserve the right to modify these Terms and Conditions at any time.
            </p>
            <p className="text-vintage-black text-lg leading-relaxed">
              Continued use of the service after any changes constitutes acceptance of the revised terms.
            </p>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              10. Governing Law
            </h2>
            <p className="text-vintage-black text-lg leading-relaxed mb-2">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of Thailand.
            </p>
            <p className="text-vintage-black text-lg leading-relaxed">
              Any disputes shall be subject to the jurisdiction of the Thai courts.
            </p>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-vintage-red mb-4">
              11. Acceptance of Terms
            </h2>
            <p className="text-vintage-black text-lg leading-relaxed">
              By clicking to start using the Photobooth service, users acknowledge that they have read, understood, and agreed to all of these Terms and Conditions.
            </p>
          </section>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Contact */}
          <section className="mb-8">
            <p className="text-vintage-black text-lg leading-relaxed mb-2">
              For further inquiries, please contact:
            </p>
            <p className="text-vintage-red text-lg font-semibold" ref={trackingRef}>
              keptscene@gmail.com
            </p>
          </section>
        </div>
      </div>
    </PageTransition>
  );
};